import type { ContractLifecycleState } from "@/components/LifecycleStateBadge";

export type WorkflowStage =
  | "draft"
  | "internal_review"
  | "ready_to_send"
  | "negotiation"
  | "signature"
  | "executed";

export type ApprovalStageId =
  | "finance_review"
  | "hr_review"
  | "sport_admin_review"
  | "ad_review"
  | "legal_review"
  | "signature"
  | "executed"
  | "draft";

export type ApprovalStatusLight = {
  hasApproval: boolean;
  status: "none" | "in_progress" | "returned" | "rejected" | "executed";
  currentStage: ApprovalStageId | null;
};

export interface WorkflowStageMeta {
  id: WorkflowStage;
  label: string;
  /** Short label for compact chips. */
  shortLabel: string;
  /** Tab anchor on the contract detail page that this stage maps to. */
  tabHash: string;
  /** Tone token used for color theming. */
  tone: "neutral" | "info" | "warning" | "primary" | "success";
}

export const WORKFLOW_STAGES: WorkflowStageMeta[] = [
  { id: "draft",            label: "Draft",            shortLabel: "Draft",     tabHash: "details",     tone: "neutral" },
  { id: "internal_review",  label: "Internal Review",  shortLabel: "In Review", tabHash: "approval",    tone: "warning" },
  { id: "ready_to_send",    label: "Ready to Send",    shortLabel: "Ready",     tabHash: "approval",    tone: "info"    },
  { id: "negotiation",      label: "Negotiation",      shortLabel: "Negotiation", tabHash: "negotiation", tone: "info"  },
  { id: "signature",        label: "Signature",        shortLabel: "Signing",   tabHash: "approval",    tone: "primary" },
  { id: "executed",         label: "Executed",         shortLabel: "Executed",  tabHash: "details",     tone: "success" },
];

export const WORKFLOW_STAGE_META: Record<WorkflowStage, WorkflowStageMeta> = WORKFLOW_STAGES.reduce(
  (acc, s) => { acc[s.id] = s; return acc; },
  {} as Record<WorkflowStage, WorkflowStageMeta>,
);

/**
 * Map lifecycle state + approval engine state into the 6 high-level workflow
 * stages used by the status bar, the context-aware CTA, and the contracts
 * list badge. The lifecycle and approval flows are intentionally separate
 * concerns server-side; this helper is the single source of truth used to
 * present them to the user as one coherent workflow.
 *
 * Optional `ctx` makes term sheets share the same stage ids while following
 * their lighter spine: internal clearance → ready to send → negotiation →
 * accepted (mapped to "executed" for the bar; promote is the next product step).
 */
export function computeWorkflowStage(
  lifecycleState: ContractLifecycleState,
  approval: ApprovalStatusLight | null,
  ctx?: { documentKind?: string | null; termSheetStatus?: string | null },
): WorkflowStage {
  const isTermSheet = ctx?.documentKind === "term_sheet";
  const tsStatus = ctx?.termSheetStatus ?? null;

  if (isTermSheet) {
    // Candidate outcome wins — promote (or closed declined) is the next step.
    if (tsStatus === "accepted" || tsStatus === "declined") return "executed";
  }

  // Once sent externally, negotiation wins over internal approval labels.
  if (lifecycleState === "out_for_negotiation" || lifecycleState === "counter_received") {
    return "negotiation";
  }

  if (isTermSheet) {
    if (tsStatus === "sent" || tsStatus === "countered") return "negotiation";

    const PRE_SEND: ContractLifecycleState[] = ["draft", "internal_review", "legal_review"];
    if (
      (approval?.status === "executed" || tsStatus === "internally_approved") &&
      PRE_SEND.includes(lifecycleState)
    ) {
      return "ready_to_send";
    }

    if (
      approval?.status === "in_progress" ||
      approval?.status === "returned" ||
      approval?.status === "rejected"
    ) {
      return "internal_review";
    }
    if (lifecycleState === "internal_review" || lifecycleState === "legal_review") {
      return "internal_review";
    }
    return "draft";
  }

  // Executed wins over everything else (contracts).
  if (approval?.status === "executed" || lifecycleState === "signed") return "executed";

  // Lifecycle "locked" / "approved" represent the post-negotiation
  // lock-in phase: legal locked the doc for signing, or the counterparty
  // agreed to the latest terms. Either way, signers are queued or
  // actively signing. This MUST be checked before the "ready_to_send"
  // branch below, because a signature-phase approval row also reports
  // currentStage="signature" and would otherwise mis-label these
  // contracts as Ready-to-Send (and let the header CTA open the
  // external-send dialog from the wrong state).
  if (lifecycleState === "locked" || lifecycleState === "approved") return "signature";

  // Approval has cleared internal review (currentStage advanced to the
  // signature/executed phase) but the contract has not been sent
  // externally yet — surface as "Ready to Send" so the next action
  // (external send) is obvious. We also gate this on the lifecycle
  // being in a pre-send state so a signature-phase approval that
  // somehow co-exists with a post-send lifecycle (e.g. legacy data)
  // can never resolve to ready_to_send.
  const PRE_SEND: ContractLifecycleState[] = ["draft", "internal_review", "legal_review"];
  if (
    approval?.hasApproval &&
    approval.status === "in_progress" &&
    (approval.currentStage === "signature" || approval.currentStage === "executed") &&
    PRE_SEND.includes(lifecycleState)
  ) {
    return "ready_to_send";
  }

  // Internal approvals still in flight / returned for changes / rejected.
  if (approval?.status === "in_progress" || approval?.status === "returned" || approval?.status === "rejected") {
    return "internal_review";
  }

  // Lifecycle-driven internal review fallback when there's no approval row yet.
  if (lifecycleState === "internal_review" || lifecycleState === "legal_review") return "internal_review";

  return "draft";
}

/**
 * Derive the "current step" purely from lifecycleState, used in the contracts
 * list where the heavier approval payload isn't available per row.
 */
export function workflowStageFromLifecycle(lifecycleState: ContractLifecycleState): WorkflowStage {
  switch (lifecycleState) {
    case "draft": return "draft";
    case "internal_review": return "internal_review";
    case "legal_review": return "internal_review";
    case "out_for_negotiation":
    case "counter_received":
      return "negotiation";
    case "locked":
    case "approved":
      return "signature";
    case "signed":
      return "executed";
    default:
      return "draft";
  }
}

export function getStageIndex(stage: WorkflowStage): number {
  return WORKFLOW_STAGES.findIndex((s) => s.id === stage);
}

export function approvalCleared(approval: ApprovalStatusLight | null): boolean {
  if (!approval || !approval.hasApproval) return false;
  if (approval.status === "executed") return true;
  if (approval.status === "in_progress" && (approval.currentStage === "signature" || approval.currentStage === "executed")) return true;
  return false;
}

const APPROVAL_STAGE_LABEL: Record<ApprovalStageId, string> = {
  draft: "Draft",
  finance_review: "Finance Review",
  hr_review: "HR Review",
  sport_admin_review: "Sport Admin Review",
  legal_review: "Legal Review",
  ad_review: "AD Review",
  signature: "Signature",
  executed: "Executed",
};

export function approvalStageLabel(stage: ApprovalStageId | null | undefined): string {
  if (!stage) return "—";
  return APPROVAL_STAGE_LABEL[stage] ?? stage;
}
